<script setup lang="ts">
import {
  alertCircle,
  mailUnread,
  checkmarkCircleOutline,
  arrowBack,
  cloudUploadOutline,
  checkmarkCircle,
} from "ionicons/icons";
import { importVisitors, sendEmailWithExampleFile } from "@/api/visitors";
import { useToasters } from "@/composition/toasters";
import { useContextStore } from "@/stores/context";
import {
  IonPage,
  IonContent,
  IonLoading,
  useIonRouter,
  IonItem,
  IonModal,
  IonIcon,
  IonButton,
  IonFooter,
  IonLabel,
  IonNote,
  onIonViewWillEnter,
  IonText,
  IonFabButton,
  IonProgressBar,
} from "@ionic/vue";
import { reactive, ref } from "vue";
import { IVisitorImportParams } from "@/models/visitor";
import isDate from "@/utils/isDate";
import isEmail from "@/utils/isEmail";
import isString from "@/utils/isString";
import isPhoneNumber from "@/utils/isPhoneNumber";
import Excel from "exceljs";
import { useUserStore } from "@/stores/user";
import { useWindowWidth } from "@/composition/window";
import { event as gtag_event } from "vue-gtag";
import { Storage } from "@ionic/storage";

const storage = new Storage();
await storage.create();

const modal = ref(null as any);
const contextStore = useContextStore();
const userStore = useUserStore();
const router = useIonRouter();
const toasters = useToasters();
const { isMobile } = useWindowWidth();

const isDragging = ref(false);
const file = ref(null);

const state = reactive({
  loading: false,
  error_lines: [] as object[],
  warning_lines: [] as object[],
  modalPreview: false,
  confirmOpen: false,
  total_visitors_imported: 0,
  error_message: {
    row_number: "",
    email: "",
    name: "",
    phone_number: "",
    country_code: "",
    birthdate: "",
    company: "",
    job_title: "",
  } as IVisitorImportParams,
  preview_import: true,
  file_is_valid: false,
  visitors_to_upload: [] as IVisitorImportParams[],
  count_new_visitors: 0,
  import_in_progress: false,
  count_preview_retries: 0,
  count_import_retries: 0,
});

onIonViewWillEnter(async () => {
  await getImportInProgress();
});

const checkImportProgress = async () => {
  state.loading = true;
  await getImportInProgress().then(() => {
    importNewVisitors(false);
  });
  state.loading = false;
};

const readFile = async ($event: Event) => {
  state.visitors_to_upload = [];
  const form = new FormData();
  state.error_lines = [];

  gtag_event("import_file_selected", {
    category: "check-in app",
    method: "Mobile App",
    label: "Import file selected",
  });

  const files =
    $event.type === "drop"
      ? (($event as DragEvent).dataTransfer as DataTransfer).files
      : ($event.target as HTMLInputElement).files;

  if (files && files.length) {
    state.loading = true;

    const file = files[0];
    form.append("file", file);

    const workbook = new Excel.Workbook();
    const reader = new FileReader();

    reader.readAsArrayBuffer(file);
    reader.onload = () => {
      const buffer = reader.result;
      workbook.xlsx
        .load(buffer)
        .then((workbook) => {
          workbook.eachSheet((sheet) => {
            sheet.eachRow((row, index: number) => {
              let new_visitor = {
                email: row.getCell(1).text,
                name: row.getCell(2).text,
                phone_number: row.getCell(3).text,
                country_code: row.getCell(4).text,
                birthdate: row.getCell(5).text,
                company: row.getCell(6).text,
                job_title: row.getCell(7).text,
              };
              state.visitors_to_upload.push(new_visitor);

              // if (!validateVisitor(new_visitor, index)) {
              //   Object.keys(state.error_message).forEach((key) => {
              //     if (state.error_message[key] && key !== "row_number") {
              //       state.error_lines.push({
              //         line: state.error_message.row_number,
              //         error: state.error_message[key],
              //       });
              //     }
              //   });
              // } else {
              //   state.visitors_to_upload.push(new_visitor);
              // }
            });
          });
        })
        .then(() => {
          state.loading = false;

          if (state.error_lines.length > 0) {
            state.modalPreview = true;
          }

          if (
            state.error_lines.length === 0 &&
            state.warning_lines.length === 0 &&
            state.visitors_to_upload
          ) {
            importNewVisitors(state.preview_import);
          }
        });
    };
  }
};

const getImportInProgress = async () => {
  const import_in_progress = await storage.get("import_in_progress");

  if (import_in_progress !== null) {
    state.count_import_retries = 1;
    state.import_in_progress = true;
    state.visitors_to_upload = JSON.parse(import_in_progress);
  } else {
    state.preview_import = true;
    state.visitors_to_upload = [];
  }
};

const importNewVisitors = async (preview: boolean) => {
  state.modalPreview = false;
  state.preview_import = preview;
  state.loading = state.preview_import;

  if (!preview) {
    state.import_in_progress = true;
    if (state.count_import_retries < 1) {
      await storage.set(
        "import_in_progress",
        JSON.stringify(state.visitors_to_upload)
      );
      toasters.addSuccess(
        "Your import has started. We will send you a email confirmation as soon the import is complete."
      );
      state.count_import_retries += 1;
    }
  } else {
    state.loading = true;
  }

  if (!state.preview_import) {
    gtag_event("import_file_started", {
      category: "check-in app",
      label: "Import file started",
    });
  }

  importVisitors(
    state.visitors_to_upload,
    contextStore.event.id,
    state.preview_import
  )
    .then(async (response) => {
      if (response.status === 204) {
        state.modalPreview = false;

        if (!preview) {
          state.loading = false;
          toasters.addWarning(
            "Your import is in progress, please check again in a few minutes."
          );
        } else {
          importNewVisitors(preview);
        }
      }
      if (response.status === 201) {
        state.modalPreview = false;

        if (state.count_preview_retries < 1) {
          setTimeout(function () {
            importNewVisitors(preview);
            state.count_preview_retries += 1;
          }, 3 * 1000);
        }
      }
      if (response.status === 200) {
        if (state.preview_import) {
          if (response.data.error) {
            state.error_lines = response.data.error_lines;
            state.loading = false;
            state.modalPreview = true;
          }
          if (
            response.data.error === 0 &&
            response.data.error_lines.length === 0
          ) {
            state.loading = false;
            state.modalPreview = true;
            state.file_is_valid = true;
          }
          state.count_new_visitors =
            state.visitors_to_upload.length - state.error_lines.length;
        } else {
          state.modalPreview = false;
          state.loading = false;
          state.import_in_progress = false;
          storage.remove("import_in_progress");
          router.push(`/event/${contextStore.event.id}/visitors`);
          state.count_new_visitors = response.data.new;

          if (state.count_new_visitors > 0) {
            toasters.addSuccess(
              `${state.count_new_visitors} visitors imported successfully and the QR Code were sent."`
            );
            await storage.remove("import_in_progress");
          } else {
            toasters.addError(
              "No visitors were imported. Please review your file and try again."
            );
          }
        }
      }
    })
    .catch((error) => {
      state.modalPreview = false;
      state.loading = false;
      // Check the case for a beamian domain error
      if (error.data._errors) {
        let errorMessage = "";
        for (let err of error.data._errors) {
          errorMessage += err.message;
        }
        toasters.addError(errorMessage);
      } else {
        toasters.addError("An error has occured. Please try again later.");
      }
    });
};

const validateVisitor = (
  visitor: IVisitorImportParams,
  index: number | string
) => {
  const invalid_string_message = " is an invalid string";
  const invalid_email_message = " is an invalid email";
  const invalid_phone_number = " is an invalid phone number";
  const invalid_date = " is an invalid date";
  const cannot_be_blank = " can not be blank";
  let visitorIsValid = true;

  resetErrorData();

  state.error_message.row_number = index;

  // Required fields - email and name
  if (visitor.email === undefined || visitor.email === "") {
    state.error_message.email = "Field 'Email' " + cannot_be_blank;
    visitorIsValid = false;
  } else if (!isEmail(visitor.email)) {
    // Check if is a valid email
    state.error_message.email = visitor.email + invalid_email_message;
    visitorIsValid = false;
  }

  if (visitor.name === undefined || visitor.name === "") {
    state.error_message.name = "Field 'Name' " + cannot_be_blank;
    visitorIsValid = false;
  } else if (!isString(visitor.name)) {
    // Check if is a valid string
    state.error_message.name = visitor.name + invalid_string_message;
    visitorIsValid = false;
  }
  if (visitor.country_code != "" && !isString(visitor.country_code)) {
    state.error_message.country_code =
      visitor.country_code + invalid_string_message;
    visitorIsValid = false;
  }
  if (
    visitor.phone_number != "" &&
    !isPhoneNumber(visitor.phone_number as string)
  ) {
    state.error_message.phone_number =
      visitor.phone_number + invalid_phone_number;
    visitorIsValid = false;
  }
  if (visitor.birthdate != "" && !isDate(visitor.birthdate as string)) {
    state.error_message.birthdate = visitor.birthdate + invalid_date;
    visitorIsValid = false;
  }

  return visitorIsValid;
};

const resetErrorData = () => {
  state.error_message = {
    row_number: "",
    email: "",
    name: "",
    phone_number: "",
    country_code: "",
    birthdate: "",
    company: "",
    job_title: "",
  };
};

const openFileDialog = () => {
  document.getElementById("file-upload")?.click();
};

const confirmSendEmail = (value: boolean) => {
  state.confirmOpen = value;
};

const sendEmailExampleFile = () => {
  const event_id = contextStore.event.id;

  state.loading = true;
  sendEmailWithExampleFile(event_id as number)
    .then((response) => {
      state.confirmOpen = false;
      state.loading = false;

      if (response.status === 200) {
        toasters.addSuccess("An email with the example file was sent to you");
      } else {
        toasters.addWarning("Could not get the example file");
      }
    })
    .catch(() => {
      state.confirmOpen = false;
      state.loading = false;
      toasters.addWarning("Could not get the example file");
    });
};

const dragover = (e: Event) => {
  e.preventDefault();
  isDragging.value = true;
};

const dragleave = (e: Event) => {
  isDragging.value = false;
};

const drop = (e: DragEvent) => {
  e.preventDefault();
  if (file.value && e.dataTransfer) {
    (file.value as HTMLInputElement).files = e.dataTransfer.files;
    readFile(e);
  }

  isDragging.value = false;
};

const tryImportFileAgain = () => {
  state.modalPreview = false;
  location.reload();
};
</script>
<template>
  <ion-page>
    <ion-content>
      <div class="container">
        <div
          class="flex-desktop"
          style="align-items: center"
          :style="isMobile ? '' : 'position: relative; left: -75px;'"
        >
          <ion-fab-button
            v-if="!isMobile"
            color="primary"
            style="margin-right: 20px"
            @click="$router.go(-1)"
          >
            <ion-icon :icon="arrowBack"></ion-icon>
          </ion-fab-button>
          <h1 class="ion-margin-bottom">Import your file</h1>
        </div>
        <figure class="image" style="margin: 2rem auto; width: 14rem">
          <img src="@/assets/undraw_file_synchronization.svg" alt="" />
        </figure>
        <div
          v-if="state.import_in_progress"
          class="box box_loading"
          @click="checkImportProgress()"
          style="margin: 0 0; cursor: pointer"
        >
          <h6>You have a import in progress!</h6>
          <p>Check if it's ready or browse the app freely.</p>
          <ion-progress-bar
            type="indeterminate"
            color="primary"
          ></ion-progress-bar>
        </div>
        <div
          v-if="isMobile && !state.import_in_progress"
          class="box"
          @click="openFileDialog()"
          style="margin: 0 0; cursor: pointer"
        >
          <h6>TAP TO UPLOAD FILE</h6>
          <p>Accepted files: XLSX, XLS, CSV</p>

          <input
            id="file-upload"
            type="file"
            @change="readFile"
            style="display: none"
          />
        </div>
        <div
          v-if="!isMobile && !state.import_in_progress"
          class="dragndrop"
          :class="{ dragging: isDragging }"
          @dragover="dragover"
          @dragleave="dragleave"
          @drop="drop"
        >
          <ion-icon :icon="cloudUploadOutline"></ion-icon>
          <div style="font-weight: bold; margin: 20px 0">
            Select a file or drag and drop here
          </div>
          <div>XLSX, XLS or CSV</div>
          <ion-button
            style="width: 150px; margin: 25px auto"
            color="primary"
            fill="solid"
            expand="block"
            @click="openFileDialog()"
          >
            SELECT FILE
          </ion-button>
          <input
            id="file-upload"
            ref="file"
            type="file"
            @change="readFile"
            style="display: none"
          />
        </div>
        <ion-button
          color="primary"
          fill="clear"
          expand="block"
          @click="checkImportProgress()"
          style="margin-bottom: 2rem"
          v-if="state.import_in_progress"
        >
          CLICK HERE TO CHECK YOUR IMPORT
        </ion-button>

        <ion-button
          color="primary"
          fill="clear"
          expand="block"
          @click="confirmSendEmail(true)"
          style="margin-bottom: 2rem"
          v-else
        >
          <ion-icon slot="start" :icon="mailUnread"></ion-icon>
          Send template file to my email
        </ion-button>
      </div>
      <div class="container">
        <h5 style="margin-bottom: 1rem"><b>Steps to import visitors</b></h5>
        <p>
          Read our article
          <ion-text color="primary">
            <a
              href="https://beamian-kb.atlassian.net/servicedesk/customer/portal/9/topic/0bf8bc8c-8e4e-48a0-9cde-1ab3cd7db1cd/article/1344226"
              target="_blank"
            >
              <b>How to import the guest list file</b> </a
            >.
          </ion-text>
        </p>
        <p>
          Create a <b>spreadsheet</b> with the information of the visitors you
          want to import. Don't write any titles on the first row. Include the
          answers in the following order:
        </p>
        <p></p>

        <ul>
          <li>Email (mandatory)</li>
          <li>Name (mandatory)</li>
          <li>Phone number*</li>
          <li>Country code</li>
          <li>Birthdate (YYYY-MM-DD)</li>
          <li>Company</li>
          <li>Job title</li>
        </ul>
        <p>
          <sub
            >* Please enter your phone number prefixed with the country code
            (e.g. +1 for USA)
          </sub>
        </p>
      </div>

      <!-- <ion-grid>
        <ion-row>
          <ion-col>
            <span>Column A</span>
            <h6>Guest name</h6>
            <p><span>Row 1</span> Alexander</p>
            <p><span>Row 2</span> Amelia</p>
          </ion-col>
          <ion-col>
            <p>Name <ion-icon :icon="chevronDown"></ion-icon></p>
          </ion-col>
        </ion-row>
      </ion-grid> -->
    </ion-content>
    <ion-modal
      ref="confirm"
      :keep-contents-mounted="true"
      :is-open="state.confirmOpen"
    >
      <ion-page>
        <ion-content>
          <section>
            <div class="container">
              <h5>Send template file to email</h5>
              <p>
                An email with the template file will be sent to
                <b>{{ userStore.me.email }}</b
                >.
              </p>
            </div>
          </section>
        </ion-content>
        <ion-footer>
          <div class="container">
            <section>
              <ion-button expand="full" @click="sendEmailExampleFile()">
                Send
              </ion-button>
              <ion-button expand="full" @click="confirmSendEmail(false)">
                Cancel
              </ion-button>
            </section>
          </div>
        </ion-footer>
      </ion-page>
    </ion-modal>
    <ion-modal
      ref="modal"
      :keep-contents-mounted="true"
      :is-open="state.modalPreview"
    >
      <ion-page>
        <ion-content>
          <section>
            <div
              :class="isMobile ? 'container' : ''"
              v-if="!state.file_is_valid"
            >
              <figure class="image ion-padding">
                <img src="@/assets/undraw_access_denied.svg" alt="" />
              </figure>
              <h5>There were some errors in your file</h5>
              <div class="box error_box">
                <ion-label v-if="state.count_new_visitors">
                  <ion-icon
                    :icon="checkmarkCircle"
                    color="success"
                    size="small"
                    style="margin-block: -3px"
                  ></ion-icon>
                  <span
                    >&nbsp;{{ state.count_new_visitors }} visitors ready to be
                    imported
                  </span>
                  <br
                /></ion-label>

                <ion-label>
                  <ion-icon
                    :icon="alertCircle"
                    color="danger"
                    size="small"
                    style="margin-block: -3px"
                  ></ion-icon>
                  <span
                    >&nbsp;{{ state.error_lines.length }} lines with error
                  </span>
                </ion-label>
              </div>
              <p class="errors_note">
                Note that rows containing errors <b>will not be imported</b>.
                Please fix these lines and try again.
              </p>
            </div>

            <div
              :class="isMobile ? 'container main_message' : 'text-center'"
              v-if="state.file_is_valid"
            >
              <ion-icon
                class="verified_file"
                :icon="checkmarkCircleOutline"
                color="primary"
              ></ion-icon>
              <h5 class="file_ready">Your file is ready to be imported!</h5>
              <p class="file_ready">
                {{ state.count_new_visitors }} visitors ready to be imported
              </p>
            </div>

            <template
              v-for="(warning_line, index) in state.warning_lines"
              :key="`import-warning-${index}`"
            >
              <ion-item
                class="ion-invalid"
                v-for="warning in Object.keys(warning_line)"
                :key="`import-warning-line-${warning}`"
              >
                <ion-label color="warning">
                  <ion-icon :icon="warning"></ion-icon>
                  Line {{ warning }}</ion-label
                >
                <ion-note slot="end">
                  {{ warning_line[warning] }}
                </ion-note>
              </ion-item>
            </template>

            <template
              v-for="(error_line, index) in state.error_lines"
              :key="`import-error-${index}`"
            >
              <ion-item class="ion-invalid">
                <ion-label color="danger">
                  <ion-icon :icon="alertCircle"></ion-icon>
                  Line {{ error_line.line }}</ion-label
                >
                <ion-note slot="end">
                  {{ error_line.error }}
                </ion-note>
              </ion-item>
            </template>
            <div v-if="!isMobile" style="width: 300px; margin: 0 auto">
              <div>
                <section v-if="state.file_is_valid">
                  <ion-button expand="block" @click="importNewVisitors(false)">
                    Import File
                  </ion-button>
                </section>
              </div>

              <div>
                <section v-if="!state.file_is_valid">
                  <ion-button
                    color="warning"
                    expand="block"
                    @click="importNewVisitors(false)"
                  >
                    IMPORT FILE ANYWAY
                  </ion-button>
                  <ion-button
                    fill="clear"
                    expand="block"
                    @click="tryImportFileAgain"
                  >
                    TRY AGAIN
                  </ion-button>
                </section>
              </div>
            </div>
            <div
              v-if="state.total_visitors_imported > state.error_lines.length"
              :class="isMobile ? 'container' : ''"
              style="margin-top: 10px"
            >
              <p>The remaining visitors were imported successfuly.</p>
            </div>
          </section>
        </ion-content>

        <div v-if="isMobile">
          <div class="container main_message">
            <section v-if="state.file_is_valid">
              <ion-button expand="block" @click="importNewVisitors(false)">
                Import File
              </ion-button>
            </section>
          </div>

          <div class="container main_message">
            <section v-if="!state.file_is_valid">
              <ion-button
                color="warning"
                expand="block"
                @click="importNewVisitors(false)"
                v-if="state.count_new_visitors"
              >
                IMPORT FILE ANYWAY
              </ion-button>
              <ion-button
                fill="clear"
                expand="block"
                @click="tryImportFileAgain"
              >
                TRY AGAIN
              </ion-button>
            </section>
          </div>
        </div>
      </ion-page>
    </ion-modal>
    <ion-loading :is-open="state.loading" message="Please wait...">
    </ion-loading>
  </ion-page>
</template>
<style scoped lang="scss">
.verified_file {
  font-size: 150px !important;
  color: #2dd36f;
  margin-left: 28%;
}
.section {
  text-align: center;
}
.file_ready {
  text-align: center;
}
.main_message {
  margin-top: 35%;
}
.dragndrop {
  height: 250px;
  width: 600px;
  border-radius: 15px;
  border: dashed 1px rgba(0, 0, 0, 0.4);
  margin-top: 20px;
  margin-left: auto;
  margin-right: auto;
  text-align: center;
}

.dragndrop.dragging {
  border-color: #7165e3 !important;
  background-color: #f5f7fa !important;
}

.dragndrop ion-icon {
  margin-top: 30px;
  color: rgba(0, 0, 0, 0.6);
  font-size: 52px;
}

ion-note {
  max-width: 60%;
}

.error_box {
  border: 1px solid #757d8830 !important;
}
.errors_note {
  margin-top: 10px;
  font-size: 12px;
}

.box_loading {
  border: none;
}

@media (min-width: 768px) {
  .verified_file {
    font-size: 130px !important;
    margin-left: auto !important;
    margin-right: auto !important;
    margin-top: 100px;
  }
}
</style>
